import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ColumnDef, Row } from '@tanstack/react-table';
import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';
import { DataTable } from 'components/table/DataTable';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { useProfitabilityBreakdownRows, useProfitabilityStatusFilterCounts } from 'components/profitability/queryHooks';
import { useProfitabilityColumns } from 'components/profitability/buildProfitabilityColumns';
import { ProfitabilityBreakdownInfoContent } from 'components/profitability/ProfitabilityBreakdownInfoContent';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { ProfitabilityStatusFilterCard } from '../shared/ProfitabilityStatusFilterCard';
import { ProfitabilityBreakdownRow, ProfitabilityStatus, SupportedAttributeCode } from 'components/profitability/types';
import { useDataEntriesInTables } from 'components/table/useDataEntriesInTables';
import { useDatePickerLabels } from 'hooks/useDatePickerLabels';

export function ProfitabilityBreakdownPage() {
  const { attributeCode = 'prdCategory' } = useParams<{ attributeCode: SupportedAttributeCode }>();
  const navigate = useNavigate();
  const location = useLocation();
  const datePickerLabels = useDatePickerLabels();

  const titleId = `profitability.${attributeCode}.title`;
  const dimensionLabelId = `profitability.drillDown.dimension.${attributeCode}`;

  const { setContent } = useInfoDrawer();

  useEffect(() => {
    setContent({
      titleKey: 'profitability.deepDive.title',
      content: <ProfitabilityBreakdownInfoContent />,
    });
    return () => setContent(null);
  }, [setContent]);

  const [statusFilter, setStatusFilter] = useState<ProfitabilityStatus | null>(null);

  const toggleFilter = (filter: ProfitabilityStatus) => {
    setStatusFilter(prev => (prev === filter ? null : filter));
  };

  const { data, isLoading, isError, error } = useProfitabilityBreakdownRows(
    attributeCode,
    `profitability-${attributeCode}`
  );
  const {
    data: kpiData,
    isLoading: isKpiLoading,
    isError: isKpiError,
    error: kpiError,
  } = useProfitabilityStatusFilterCounts(attributeCode, `profitability-kpi-${attributeCode}`);
  const { getAttributeArrayForLeadAttribute, createAttributeColumn } = useDataEntriesInTables();
  const tableAttributes = getAttributeArrayForLeadAttribute(attributeCode);
  const attributeColumns = tableAttributes.map(attribute => createAttributeColumn(attribute));
  const profitabilityColumns = useProfitabilityColumns();

  const columns = [...attributeColumns, ...profitabilityColumns] as ColumnDef<ProfitabilityBreakdownRow>[];

  const filteredRows = useMemo(() => {
    if (!data) return [];
    if (statusFilter === ProfitabilityStatus.AT_RISK) return data.filter(r => r.status === ProfitabilityStatus.AT_RISK);
    if (statusFilter === ProfitabilityStatus.CRITICAL)
      return data.filter(r => r.status === ProfitabilityStatus.CRITICAL);
    return data;
  }, [data, statusFilter]);

  const handleRowClick = (row: Row<ProfitabilityBreakdownRow>) => {
    const dimensionValue = encodeURIComponent(row.original[attributeCode] ?? '');
    navigate(`/profitability/drilldown/${attributeCode}/${dimensionValue}${location.search}`);
  };

  const combinedIsLoading = isLoading || isKpiLoading;
  const combinedIsError = isError || isKpiError;
  const combinedError = error ?? kpiError;

  return (
    <div className="px-10 max-w-none">
      <PageContainer>
        <PageSectionTitle titleId={titleId} additionalTitleId={datePickerLabels.dateRangeLabel ?? undefined} />
        {combinedIsError ? (
          <Tile errorDetails={combinedError} testId={`profitability-${attributeCode}-error`} />
        ) : combinedIsLoading || data === null || kpiData === null ? (
          <Tile>
            <MetricsLoadingSpinner />
          </Tile>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 w-full xl:col-span-12 col-span-6">
              <ProfitabilityStatusFilterCard
                count={kpiData.atRiskCount}
                previousCount={kpiData.atRiskCountPrev}
                titleId="profitability.drillDown.atRisk.title"
                dimensionLabelId={dimensionLabelId}
                selected={statusFilter === ProfitabilityStatus.AT_RISK}
                onClick={() => toggleFilter(ProfitabilityStatus.AT_RISK)}
                testId={`profitability-${attributeCode}-at-risk-card`}
              />
              <ProfitabilityStatusFilterCard
                count={kpiData.criticalCount}
                previousCount={kpiData.criticalCountPrev}
                titleId="profitability.drillDown.critical.title"
                dimensionLabelId={dimensionLabelId}
                selected={statusFilter === ProfitabilityStatus.CRITICAL}
                onClick={() => toggleFilter(ProfitabilityStatus.CRITICAL)}
                testId={`profitability-${attributeCode}-critical-card`}
              />
            </div>
            <DataTableContextProvider>
              <DataTable
                titleId={titleId}
                rows={filteredRows}
                columns={columns}
                defaultSortingState={[{ id: 'netSlsGmvValExtExpOD', desc: true }]}
                onRowClick={handleRowClick}
                exportConfigOptions={{
                  exportName: `profitability-${attributeCode}`,
                  customHeaderExporters: {
                    status: exportIntl => exportIntl.formatMessage({ id: 'profitability.drillDown.column.status' }),
                  },
                }}
                testId={`profitability-${attributeCode}-table`}
              />
            </DataTableContextProvider>
          </>
        )}
      </PageContainer>
    </div>
  );
}
